import { PlanType } from '~/generated/api-clients-generated'

export type PdpPushToCsCategory = 'needsAssessment' | 'quote' | 'compare' | 'details'

export type PdpPushToCsItem = 'footer' | 'chat' | 'helperCard'

export type PdpPushToCsIndicator = {
  isHide?: boolean
  pathWhiteList?: string[]
  includedPaths?: string[]
}

export default class QuotingConfig {
  static use() {
    const { flag } = FeatureFlags.use()
    const { path } = AppRoute.use()
    const session = SessionStore.use()
    const sessionPlanType = computed(() => session.planType)

    const pdpPushToCsConfigRaw = computed(
      () => flag<Record<PdpPushToCsCategory, any>>('pdp-push-to-cs-config').value
    )

    const needsAssessmentConfig: globalThis.ComputedRef<Record<PdpPushToCsItem, any>> = computed(
      () => pdpPushToCsConfigRaw.value.needsAssessment ?? {}
    )
    const quoteConfig: globalThis.ComputedRef<Record<PdpPushToCsItem, any>> = computed(
      () => pdpPushToCsConfigRaw.value.quote ?? {}
    )
    const compareConfig: globalThis.ComputedRef<Record<PdpPushToCsItem, any>> = computed(
      () => pdpPushToCsConfigRaw.value.compare ?? {}
    )
    const detailsConfig: globalThis.ComputedRef<Record<PdpPushToCsItem, any>> = computed(
      () => pdpPushToCsConfigRaw.value.details ?? {}
    )

    const makeIndicator = (
      configRef: globalThis.ComputedRef<Record<PdpPushToCsItem, any>>,
      item: PdpPushToCsItem,
      defaultIncludedPaths: Array<string> = []
    ): globalThis.ComputedRef<PdpPushToCsIndicator> =>
      computed(() => {
        const raw = configRef.value[item] ?? {}
        return { includedPaths: defaultIncludedPaths, pathWhiteList: [], ...raw }
      })

    const needsAssessmentDefaultPaths = ['/drugs', '/medicaid', '/pdp-other-plans']
    const needsAssessmentFooterIndicator = makeIndicator(
      needsAssessmentConfig,
      'footer',
      needsAssessmentDefaultPaths
    )
    const needsAssessmentChatIndicator = makeIndicator(
      needsAssessmentConfig,
      'chat',
      needsAssessmentDefaultPaths
    )
    const needsAssessmentHelperCardIndicator = makeIndicator(
      needsAssessmentConfig,
      'helperCard',
      needsAssessmentDefaultPaths
    )

    const quoteDefaultPaths = ['/quotes']
    const quoteFooterIndicator = makeIndicator(quoteConfig, 'footer', quoteDefaultPaths)
    const quoteChatIndicator = makeIndicator(quoteConfig, 'chat', quoteDefaultPaths)
    const quoteHelperCardIndicator = makeIndicator(quoteConfig, 'helperCard', quoteDefaultPaths)

    const compareDefaultPaths = ['/compare']
    const compareFooterIndicator = makeIndicator(compareConfig, 'footer', compareDefaultPaths)
    const compareChatIndicator = makeIndicator(compareConfig, 'chat', compareDefaultPaths)
    const compareHelperCardIndicator = makeIndicator(
      compareConfig,
      'helperCard',
      compareDefaultPaths
    )

    const detailsDefaultPaths = ['/details']
    const detailsFooterIndicator = makeIndicator(detailsConfig, 'footer', detailsDefaultPaths)
    const detailsChatIndicator = makeIndicator(detailsConfig, 'chat', detailsDefaultPaths)
    const detailsHelperCardIndicator = makeIndicator(
      detailsConfig,
      'helperCard',
      detailsDefaultPaths
    )

    const makeIsHide = (indicatorRef: globalThis.ComputedRef<PdpPushToCsIndicator>) =>
      computed(() => {
        const { isHide, pathWhiteList = [], includedPaths = [] } = indicatorRef.value ?? {}

        const inIncludedPaths = (list: string[]) =>
          list.some(
            (base) =>
              path.value === base || path.value.startsWith(base.endsWith('/') ? base : base + '/')
          )

        // if the path is explicitly whitelisted, never hide
        if (pathWhiteList.includes(path.value)) return false
        // hide when path is explicitly included + PDP + isHide === true
        return (
          isHide === true &&
          sessionPlanType.value === PlanType.PDP &&
          inIncludedPaths(includedPaths)
        )
      })

    const isHideNeedsAssessmentCsFooter = makeIsHide(needsAssessmentFooterIndicator)
    const isHideNeedsAssessmentCsChat = makeIsHide(needsAssessmentChatIndicator)
    const isHideNeedsAssessmentCsHelperCard = makeIsHide(needsAssessmentHelperCardIndicator)

    const isHideQuoteCsFooter = makeIsHide(quoteFooterIndicator)
    const isHideQuoteCsChat = makeIsHide(quoteChatIndicator)
    const isHideQuoteCsHelperCard = makeIsHide(quoteHelperCardIndicator)

    const isHideCompareCsFooter = makeIsHide(compareFooterIndicator)
    const isHideCompareCsChat = makeIsHide(compareChatIndicator)
    const isHideCompareCsHelperCard = makeIsHide(compareHelperCardIndicator)

    const isHideDetailsCsFooter = makeIsHide(detailsFooterIndicator)
    const isHideDetailsCsChat = makeIsHide(detailsChatIndicator)
    const isHideDetailsCsHelperCard = makeIsHide(detailsHelperCardIndicator)

    const isHideCsFooter = computed(
      () =>
        isHideNeedsAssessmentCsFooter.value ||
        isHideQuoteCsFooter.value ||
        isHideCompareCsFooter.value ||
        isHideDetailsCsFooter.value
    )

    const isHideCsChat = computed(
      () =>
        isHideNeedsAssessmentCsChat.value ||
        isHideQuoteCsChat.value ||
        isHideCompareCsChat.value ||
        isHideDetailsCsChat.value
    )

    const isHideCsHelperCard = computed(
      () =>
        isHideNeedsAssessmentCsHelperCard.value ||
        isHideQuoteCsHelperCard.value ||
        isHideCompareCsHelperCard.value ||
        isHideDetailsCsHelperCard.value
    )

    return {
      needsAssessmentConfig,
      quoteConfig,
      compareConfig,
      detailsConfig,

      needsAssessmentChatIndicator,
      isHideNeedsAssessmentCsFooter,
      isHideNeedsAssessmentCsChat,
      isHideNeedsAssessmentCsHelperCard,

      isHideQuoteCsFooter,
      isHideQuoteCsChat,
      isHideQuoteCsHelperCard,

      isHideCompareCsFooter,
      isHideCompareCsChat,
      isHideCompareCsHelperCard,

      isHideDetailsCsFooter,
      isHideDetailsCsChat,
      isHideDetailsCsHelperCard,

      isHideCsFooter,
      isHideCsChat,
      isHideCsHelperCard
    }
  }
}
